import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, X, Car, Search, RefreshCw, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import api from '../services/api';

function AdminLicenciatarios() {
  const [licenciatarios, setLicenciatarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    cif: '',
    direccion: '',
    codigo_postal: '',
    ciudad: '',
    provincia: ''
  });
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState({ empresa_id: '', busqueda: '', comunicar_conductor_final: '' });
  const [toggling, setToggling] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [licRes, empRes] = await Promise.all([
        api.get('/admin/licenciatarios', {
          params: filtro.empresa_id ? { empresa_id: filtro.empresa_id } : {}
        }),
        api.get('/admin/empresas')
      ]);
      setLicenciatarios(licRes.data.data || []);
      setEmpresas(empRes.data.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const buscar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro.empresa_id) params.empresa_id = filtro.empresa_id;
      if (filtro.busqueda) params.busqueda = filtro.busqueda;
      if (filtro.comunicar_conductor_final !== '') params.comunicar_conductor_final = filtro.comunicar_conductor_final;

      const response = await api.get('/admin/licenciatarios', { params });
      setLicenciatarios(response.data.data || []);
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleConductorFinal = async (lic) => {
    setToggling(lic.id);
    try {
      await api.patch(`/admin/licenciatarios/${lic.id}/toggle`, {
        comunicar_conductor_final: !lic.comunicar_conductor_final
      });
      setLicenciatarios(prev =>
        prev.map(l => l.id === lic.id ? { ...l, comunicar_conductor_final: !l.comunicar_conductor_final } : l)
      );
    } catch (error) {
      alert(error.response?.data?.error || 'Error actualizando toggle');
    } finally {
      setToggling(null);
    }
  };

  const abrirModal = (lic) => {
    setEditando(lic);
    setFormData({
      nombre: lic.nombre || '',
      cif: lic.cif || '',
      direccion: lic.direccion || '',
      codigo_postal: lic.codigo_postal || '',
      ciudad: lic.ciudad || '',
      provincia: lic.provincia || ''
    });
    setError('');
    setShowModal(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.put(`/admin/licenciatarios/${editando.id}`, formData);
      setShowModal(false);
      buscar();
    } catch (error) {
      setError(error.response?.data?.error || 'Error guardando licenciatario');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white/80 backdrop-blur-lg shadow-soft border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display">Licenciatarios</h1>
              <p className="text-sm text-slate-600">Gestiona licenciatarios MyBooking y su modo de comunicacion</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Toggle "Conductor Final":</strong> Cuando esta activado, el sistema consultara la API MyBooking para obtener el conductor de la reserva en lugar de comunicar al licenciatario como empresa cesionaria.
          </p>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-soft p-4 mb-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Empresa</label>
              <select
                value={filtro.empresa_id}
                onChange={(e) => setFiltro({...filtro, empresa_id: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todas las empresas</option>
                {empresas.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Buscar</label>
              <input
                type="text"
                value={filtro.busqueda}
                onChange={(e) => setFiltro({...filtro, busqueda: e.target.value})}
                placeholder="Nombre o CIF..."
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Conductor Final</label>
              <select
                value={filtro.comunicar_conductor_final}
                onChange={(e) => setFiltro({...filtro, comunicar_conductor_final: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos</option>
                <option value="true">Activado</option>
                <option value="false">Desactivado</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={buscar}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Search className="w-4 h-4" />
                Buscar
              </button>
              <button
                onClick={() => { setFiltro({ empresa_id: '', busqueda: '', comunicar_conductor_final: '' }); cargarDatos(); }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Cargando...</p>
          </div>
        ) : licenciatarios.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft p-12 text-center border border-slate-200">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No hay licenciatarios</h3>
            <p className="text-slate-600">Los licenciatarios se importan automaticamente desde las tablas de vehiculos MyBooking.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Licenciatario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">CIF</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Vehiculos</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">Conductor Final</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {licenciatarios.map((lic) => (
                  <tr key={lic.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{lic.nombre}</div>
                          {lic.ciudad && <div className="text-sm text-slate-500">{lic.ciudad}{lic.provincia ? `, ${lic.provincia}` : ''}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-slate-900">{lic.cif}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{lic.empresa_nombre}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-100 text-violet-700 text-sm">
                        <Car className="w-4 h-4" />
                        {lic.num_vehiculos || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleConductorFinal(lic)}
                        disabled={toggling === lic.id}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          lic.comunicar_conductor_final
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        } ${toggling === lic.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                      >
                        {lic.comunicar_conductor_final
                          ? <><ToggleRight className="w-4 h-4" /> Activado</>
                          : <><ToggleLeft className="w-4 h-4" /> Desactivado</>
                        }
                      </button>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => abrirModal(lic)}
                        className="p-2 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal Editar */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">Editar Licenciatario</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-slate-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={guardar} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre / Razon Social</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">CIF</label>
                  <input
                    type="text"
                    value={formData.cif}
                    onChange={(e) => setFormData({...formData, cif: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Direccion</label>
                <input
                  type="text"
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Codigo Postal</label>
                  <input
                    type="text"
                    value={formData.codigo_postal}
                    onChange={(e) => setFormData({...formData, codigo_postal: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                    placeholder="28001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={formData.ciudad}
                    onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Provincia</label>
                  <input
                    type="text"
                    value={formData.provincia}
                    onChange={(e) => setFormData({...formData, provincia: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLicenciatarios;
